import React, { useState } from "react";
import { useDispatch } from "react-redux";
import { Link, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { registerStart, registerSuccess, registerFailure } from "../redux/authSlice";
import { setDoc, doc } from "firebase/firestore";
import { auth, db } from "../firebase/firebaseConfig";
import { createUserWithEmailAndPassword, updateProfile } from "firebase/auth";

/* ─── Design tokens ───────────────────────────────────────── */
const T = {
  rose:      "#c0506e",
  roseDark:  "#8f2d46",
  roseLight: "#e8839a",
  roseSoft:  "#fdf0f3",
  roseMid:   "#f3c5cf",
  ivory:     "#faf8f5",
  ivoryDark: "#f0ece6",
  warm100:   "#e8e3dc",
  warm200:   "#d4cfc8",
  warm400:   "#9e9189",
  warm700:   "#3d3530",
  ink:       "#1a1510",
  ffDisplay: "'Cormorant Garamond', Georgia, serif",
  ffBody:    "'Outfit', system-ui, sans-serif",
};

/* ─── Eye icon ────────────────────────────────────────────── */
function EyeIcon({ open }) {
  return open ? (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none"
      stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94"/>
      <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19"/>
      <line x1="1" y1="1" x2="23" y2="23"/>
    </svg>
  ) : (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none"
      stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
      <circle cx="12" cy="12" r="3"/>
    </svg>
  );
}

/* ─── Field component ─────────────────────────────────────── */
function Field({ label, error, hint, type = "text", rightElement, ...props }) {
  const [focused, setFocused] = useState(false);
  return (
    <div style={{ marginBottom: "1rem" }}>
      <label style={{
        display: "block",
        fontSize: 11,
        fontWeight: 600,
        letterSpacing: "0.1em",
        textTransform: "uppercase",
        color: error ? T.rose : focused ? T.rose : T.warm400,
        marginBottom: 6,
        fontFamily: T.ffBody,
        transition: "color 180ms",
      }}>{label}</label>
      <div style={{ position: "relative" }}>
        <input
          type={type}
          {...props}
          onFocus={(e) => { setFocused(true); props.onFocus?.(e); }}
          onBlur={(e) => { setFocused(false); props.onBlur?.(e); }}
          style={{
            width: "100%",
            boxSizing: "border-box",
            padding: rightElement ? "0.75rem 2.75rem 0.75rem 1rem" : "0.75rem 1rem",
            fontFamily: T.ffBody,
            fontSize: 14,
            color: T.ink,
            background: focused ? "#fff" : T.ivory,
            border: `1.5px solid ${error ? T.rose : focused ? T.rose : T.warm200}`,
            borderRadius: 10,
            outline: "none",
            transition: "border-color 200ms, background 200ms, box-shadow 200ms",
            boxShadow: focused ? `0 0 0 3px rgba(192,80,110,.12)` : "none",
          }}
        />
        {rightElement && (
          <div style={{ position: "absolute", right: 12, top: "50%", transform: "translateY(-50%)" }}>
            {rightElement}
          </div>
        )}
      </div>
      {(error || hint) && (
        <p style={{ margin: "5px 0 0 2px", fontSize: 12, color: error ? T.rose : T.warm400, fontFamily: T.ffBody }}>
          {error || hint}
        </p>
      )}
    </div>
  );
}

/* ─── Password strength indicator ────────────────────────── */
function PasswordStrength({ password }) {
  if (!password) return null;
  const strength = password.length >= 8 && /[A-Z]/.test(password) && /[0-9]/.test(password) ? "strong"
    : password.length >= 6 ? "medium" : "weak";
  const config = {
    weak:   { width: "33%",  color: "#e07070", label: "Weak" },
    medium: { width: "66%",  color: "#f59e0b", label: "Medium" },
    strong: { width: "100%", color: "#22c55e", label: "Strong" },
  }[strength];
  return (
    <div style={{ marginTop: 6 }}>
      <div style={{ height: 3, background: T.warm100, borderRadius: 99, overflow: "hidden" }}>
        <div style={{ height: "100%", width: config.width, background: config.color, borderRadius: 99, transition: "width 300ms, background 300ms" }} />
      </div>
      <p style={{ margin: "3px 0 0", fontSize: 11, color: config.color, fontFamily: T.ffBody, fontWeight: 600 }}>
        {config.label} password
      </p>
    </div>
  );
}

/* ─── Friendly Firebase error messages ───────────────────── */
const friendlyError = (code = "") => {
  if (code.includes("email-already-in-use")) return "An account with this email already exists. Try signing in instead.";
  if (code.includes("invalid-email"))        return "Please enter a valid email address.";
  if (code.includes("weak-password"))        return "Password must be at least 6 characters.";
  if (code.includes("network-request-failed")) return "Network error. Please check your connection.";
  return "Registration failed. Please try again.";
};

/* ─── Main component ──────────────────────────────────────── */
const Register = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [form, setForm] = useState({ name: "", email: "", password: "", confirmPassword: "" });
  const [showPassword, setShowPassword]   = useState(false);
  const [showConfirm, setShowConfirm]     = useState(false);
  const [touched, setTouched]             = useState({});
  const [loading, setLoading]             = useState(false);
  const [error, setError]                 = useState("");

  const set = (key) => (e) => setForm((p) => ({ ...p, [key]: e.target.value }));
  const blur = (key) => () => setTouched((p) => ({ ...p, [key]: true }));
  const isValidEmail = (v) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);

  const errors = {
    name:            touched.name            && !form.name.trim()                      ? "Full name is required" : null,
    email:           touched.email           && !isValidEmail(form.email)              ? "Enter a valid email address" : null,
    password:        touched.password        && form.password.length < 6              ? "Password must be at least 6 characters" : null,
    confirmPassword: touched.confirmPassword && form.password !== form.confirmPassword ? "Passwords do not match" : null,
  };

  const canSubmit = form.name && isValidEmail(form.email) && form.password.length >= 6
    && form.password === form.confirmPassword && !loading;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setTouched({ name: true, email: true, password: true, confirmPassword: true });
    setError("");

    if (!canSubmit) return;

    setLoading(true);
    dispatch(registerStart());

    try {
      const { user } = await createUserWithEmailAndPassword(auth, form.email, form.password);
      await updateProfile(user, { displayName: form.name });
      await setDoc(doc(db, "users", user.uid), {
        uid:   user.uid,
        name:  form.name,
        email: form.email,
        role:  "user",
        createdAt: new Date(),
      });
      dispatch(registerSuccess({ uid: user.uid, email: user.email, name: form.name, role: "user" }));
      navigate("/shop");
    } catch (err) {
      const msg = friendlyError(err.code);
      setError(msg);
      dispatch(registerFailure(msg));
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>Create Account | Glossy Lux Hair</title>
        <meta name="description" content="Sign up for a free Glossy Lux Hair account and shop premium wigs and hair extensions in Ghana." />
        <link rel="canonical" href="https://glossyluxhair.com/register" />
      </Helmet>

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,600;0,700;1,600&family=Outfit:wght@400;500;600&display=swap');
        @keyframes fadeUp { from { opacity:0; transform:translateY(20px); } to { opacity:1; transform:translateY(0); } }
        @keyframes spin   { to { transform: rotate(360deg); } }
        .reg-card { animation: fadeUp 480ms cubic-bezier(0.16,1,0.3,1) both; }
        .reg-submit:not(:disabled):hover {
          background: ${T.roseDark} !important;
          box-shadow: 0 8px 28px rgba(192,80,110,.32) !important;
          transform: translateY(-2px);
        }
        .reg-submit { transition: background 220ms, box-shadow 220ms, transform 200ms; }
        .reg-submit:disabled { opacity: .5; cursor: not-allowed; }
        .eye-btn:hover { color: ${T.rose} !important; }
        .eye-btn { transition: color 180ms; }
      `}</style>

      <div style={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: `
          radial-gradient(ellipse 70% 50% at 70% 10%, rgba(192,80,110,.07) 0%, transparent 60%),
          radial-gradient(ellipse 50% 40% at 20% 90%, rgba(192,80,110,.05) 0%, transparent 55%),
          ${T.ivoryDark}
        `,
        padding: "2rem 1rem",
        fontFamily: T.ffBody,
      }}>
        <div className="reg-card" style={{
          width: "100%",
          maxWidth: 440,
          background: "#fff",
          borderRadius: 20,
          border: `1px solid ${T.warm100}`,
          boxShadow: `0 4px 24px rgba(26,21,16,.06), 0 20px 64px rgba(192,80,110,.08)`,
          overflow: "hidden",
        }}>

          {/* Accent bar */}
          <div style={{
            height: 3,
            background: `linear-gradient(90deg, transparent 0%, ${T.roseMid} 15%, ${T.rose} 50%, ${T.roseMid} 85%, transparent 100%)`,
          }} />

          <div style={{ padding: "clamp(1.75rem, 5vw, 2.5rem)" }}>

            {/* Header */}
            <div style={{ marginBottom: "1.75rem" }}>
              <Link to="/" style={{ textDecoration: "none" }}>
                <p style={{ margin: "0 0 1.1rem", fontFamily: T.ffDisplay, fontSize: "1.1rem", fontWeight: 700, color: T.rose }}>
                  GlossyLux<em style={{ fontStyle: "italic" }}>Hair</em>
                </p>
              </Link>
              <h1 style={{ margin: "0 0 .3rem", fontFamily: T.ffDisplay, fontSize: "clamp(1.7rem, 5vw, 2.1rem)", fontWeight: 700, color: T.ink, letterSpacing: "-0.02em" }}>
                Create an account
              </h1>
              <p style={{ margin: 0, fontSize: 14, color: T.warm400 }}>
                Join us and start shopping today
              </p>
            </div>

            {/* Error alert */}
            {error && (
              <div style={{
                display: "flex",
                alignItems: "flex-start",
                gap: 10,
                padding: "0.85rem 1rem",
                background: T.roseSoft,
                border: `1px solid ${T.roseMid}`,
                borderRadius: 10,
                marginBottom: "1.25rem",
              }}>
                <span style={{ color: T.rose, fontSize: 15, flexShrink: 0 }}>⚠</span>
                <p style={{ margin: 0, fontSize: 13, color: T.roseDark, fontFamily: T.ffBody }}>{error}</p>
              </div>
            )}

            {/* Form */}
            <form onSubmit={handleSubmit} noValidate>
              <Field
                label="Full Name *"
                type="text"
                placeholder="Jane Doe"
                value={form.name}
                error={errors.name}
                onChange={set("name")}
                onBlur={blur("name")}
                autoComplete="name"
              />

              <Field
                label="Email Address *"
                type="email"
                placeholder="you@example.com"
                value={form.email}
                error={errors.email}
                onChange={set("email")}
                onBlur={blur("email")}
                autoComplete="email"
              />

              <div>
                <Field
                  label="Password *"
                  type={showPassword ? "text" : "password"}
                  placeholder="At least 6 characters"
                  value={form.password}
                  error={errors.password}
                  onChange={set("password")}
                  onBlur={blur("password")}
                  autoComplete="new-password"
                  rightElement={
                    <button
                      type="button"
                      className="eye-btn"
                      onClick={() => setShowPassword((v) => !v)}
                      aria-label={showPassword ? "Hide password" : "Show password"}
                      style={{ background: "none", border: "none", cursor: "pointer", color: T.warm400, padding: 0, display: "flex" }}
                    >
                      <EyeIcon open={showPassword} />
                    </button>
                  }
                />
                <PasswordStrength password={form.password} />
              </div>

              <div style={{ marginTop: "1rem" }}>
                <Field
                  label="Confirm Password *"
                  type={showConfirm ? "text" : "password"}
                  placeholder="Repeat your password"
                  value={form.confirmPassword}
                  error={errors.confirmPassword}
                  onChange={set("confirmPassword")}
                  onBlur={blur("confirmPassword")}
                  autoComplete="new-password"
                  rightElement={
                    <button
                      type="button"
                      className="eye-btn"
                      onClick={() => setShowConfirm((v) => !v)}
                      aria-label={showConfirm ? "Hide password" : "Show password"}
                      style={{ background: "none", border: "none", cursor: "pointer", color: T.warm400, padding: 0, display: "flex" }}
                    >
                      <EyeIcon open={showConfirm} />
                    </button>
                  }
                />
              </div>

              <button
                type="submit"
                disabled={!canSubmit}
                className="reg-submit"
                style={{
                  marginTop: "1.25rem",
                  width: "100%",
                  padding: "0.9rem",
                  fontFamily: T.ffBody,
                  fontWeight: 600,
                  fontSize: 15,
                  letterSpacing: "0.04em",
                  color: "#fff",
                  background: canSubmit ? T.rose : T.warm200,
                  border: "none",
                  borderRadius: 12,
                  cursor: canSubmit ? "pointer" : "not-allowed",
                  boxShadow: canSubmit ? `0 4px 20px rgba(192,80,110,.22)` : "none",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 8,
                }}
              >
                {loading ? (
                  <>
                    <span style={{
                      width: 16, height: 16,
                      border: "2px solid rgba(255,255,255,.4)",
                      borderTopColor: "#fff",
                      borderRadius: "50%",
                      display: "inline-block",
                      animation: "spin .7s linear infinite",
                    }} />
                    Creating account…
                  </>
                ) : "Create Account →"}
              </button>
            </form>

            {/* Login link */}
            <p style={{ textAlign: "center", fontSize: 13, color: T.warm400, margin: "1.5rem 0 0" }}>
              Already have an account?{" "}
              <Link to="/login" style={{ color: T.rose, fontWeight: 600, textDecoration: "none" }}>
                Sign in →
              </Link>
            </p>

          </div>
        </div>
      </div>
    </>
  );
};

export default Register;